<template>
  <div id="live-feed">
    <Breadcrumbs
      :crumbs="[
        { name: globals.info.school_name, link: `${pathPrefix}/` },
        { name: getSectionName, link: `${pathPrefix}/live-feed` },
      ]"
    />
    <div class="page-container">
      <div
        v-if="filters && filters.length > 1"
        class="filter"
      >
        <select
          v-model="selectedFilter"
          name="filter"
          :aria-label="$t('pages.selectLiveFeed')"
          data-testid="filter-trigger-live-feed"
          @change="filterFeeds"
        >
          <option
            v-for="filter in filters"
            :key="filter.id"
            :value="filter.id"
          >
            {{ filter.name }}
          </option>
        </select>
      </div>
      <LoadingIndicator v-if="loading" />
      <div
        v-for="(liveFeed, idx) in feeds"
        v-else
        :id="liveFeed.id"
        :key="idx"
        class="page-container-item"
      >
        <div class="avatar-holder">
          <img
            alt=""
            :src="liveFeed.avatar"
            @error="setDefaultAvatar"
          />
        </div>
        <div
          class="status"
          v-html="liveFeed.status"
        />
        <div class="date-author">{{ liveFeed.time_ago }}, {{ liveFeed.author }}</div>
        <div
          v-if="liveFeed.live_feed_images.length > 0"
          class="images"
        >
          <ts-row class="images-row">
            <ts-col
              v-for="(image, index) in liveFeed.live_feed_images"
              :key="index"
              class="image-wrapper-container"
              ts-align="center"
            >
              <div class="image-wrapper">
                <img
                  :src="image.url"
                  :alt="image.alt_text"
                  @click="openLightbox($event, liveFeed.live_feed_images, index)"
                />
              </div>
            </ts-col>
          </ts-row>
        </div>
      </div>

      <pagination
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, useStore } from 'vuex';
import tsCol from '../../components/global/tsCol.vue';
import tsRow from '../../components/global/tsRow.vue';
import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import Pagination from '../../components/global/Pagination.vue';
import { setupAndFetchLiveFeed } from '../../helpers/section/sections.helper';
import { getMetadataAndTags } from '../../helpers/metadata';

export default defineNuxtComponent({
  name: 'LiveFeed',
  components: {
    tsCol,
    tsRow,
    Breadcrumbs,
    LoadingIndicator,
    Pagination,
  },
  beforeRouteUpdate(to, from, next) {
    if (
      import.meta.client &&
      (to.query.page_no !== from.query.page_no ||
        to.query.filter_id !== from.query.filter_id ||
        to.query.page_size !== from.query.page_size)
    ) {
      this.fetchLivefeed(to.query);
      next();
    } else {
      next();
    }
  },
  data() {
    return {
      loading: false,
      feeds: [],
      filters: [],
      bottom: false,
      pageNumber: 1,
      totalPages: 1,
      selectedFilter: '',
      nextPageUrl: '',
    };
  },
  async setup() {
    const store = useStore();
    const { data } = await useAsyncData(
      'live-feed',
      async () => await setupAndFetchLiveFeed(store, useRoute().query, useNuxtApp().$axios, useNuxtApp().$t),
    );
    const metaDataInfo = getMetadataAndTags(store.state.settings.metadata, 'live_feeds', store.getters.getSectionName);
    useHead({
      ...metaDataInfo.metaTitle,
      meta: metaDataInfo.metaTags,
    });
    return data.value;
  },
  computed: {
    ...mapState(['globals', 'pathPrefix']),
    ...mapGetters(['getSectionName']),
    ...mapGetters('translation', ['getLanguage']),
  },
  watch: {
    '$route.query.page_no': {
      handler(oldValue, newValue) {
        if (import.meta.client && oldValue !== newValue) {
          window.scrollTo(0, 0);
        }
      },
    },
  },
  methods: {
    async fetchLivefeed(query) {
      this.loading = true;
      this.totalPages = 0;
      const { feeds, filters, totalPages, currentPage, selectedFilter } = await setupAndFetchLiveFeed(
        this.$store,
        query,
        this.$axios,
        this.$t,
      );
      this.feeds = feeds;
      this.selectedFilter = selectedFilter;
      this.filters = filters;
      this.totalPages = totalPages;
      this.currentPage = currentPage;
      this.loading = false;
    },
    setDefaultAvatar(evt) {
      // eslint-disable-next-line no-param-reassign
      evt.target.src = 'https://www.apptegy.net/fallback/circled_default_avatar.png';
    },
    filterFeeds() {
      return navigateTo({ query: { filter_id: this.selectedFilter } });
    },
    openLightbox(e, images, startingIndex) {
      this.$store.commit('setPrevFocusedElem', e.target);
      this.$store.commit('setLightBoxCurrentIndex', startingIndex);
      this.$store.commit('openLightbox');
      this.$store.commit('setLightboxImages', images);
    },
  },
});
</script>

<style scoped lang="scss">
#live-feed {
  .page-container {
    max-width: 640px;
  }
  .page-container-item {
    padding: 10px 0;
  }
  .avatar-holder {
    float: left;
    margin: 0 15px 0 0;
    img {
      width: 70px;
      max-width: 100%;
      height: 70px;
      margin-top: 0;
      object-fit: contain;
    }
  }
  .status {
    font-size: 16px;
    :deep(a) {
      color: var(--links-color);
    }
  }
  .date-author {
    color: #595959;
    font-size: 12px;
    text-transform: uppercase;
    margin: 10px 0;
  }
  .images {
    width: 100%;
    overflow: hidden;
    font-size: 0;
    margin-top: 50px;
    margin-bottom: 25px;
    .images-row {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      .image-wrapper-container {
        flex: 1 1;
        min-width: 33%;
      }
    }
    .image-wrapper {
      margin: 5px;
      overflow: hidden;
      background-color: #f3f3f3;
      border-radius: 4px;
      img {
        width: 100%;
        aspect-ratio: 1/1;
        object-fit: contain;
        cursor: pointer;
      }
    }
  }
}
</style>
